import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { auditLogsService } from '../services/apiService';

const CaseAuditLogSearch = () => {
  const [caseNumber, setCaseNumber] = useState('');
  const [referenceLetterNumber, setReferenceLetterNumber] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  
  // Color mode values
  const oldValuesBg = useColorModeValue('gray.50', 'gray.700');
  const newValuesBg = useColorModeValue('green.50', 'green.900');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.800', 'gray.300');
  const valueColor = useColorModeValue('gray.700', 'gray.400');
  const emptyDataColor = useColorModeValue('gray.600', 'gray.400');
  const userTextColor = useColorModeValue('gray.700', 'gray.400');

  const handleSearch = async () => {
    if (!caseNumber && !referenceLetterNumber) {
      toast({
        title: 'خطأ',
        description: 'يجب إدخال رقم القضية أو رقم خطاب الإحالة',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const data = await auditLogsService.search(caseNumber, referenceLetterNumber);
      setLogs(data || []);
      
      if (data.length === 0) {
        toast({
          title: 'لا توجد نتائج',
          description: 'لم يتم العثور على أي عمليات مسجلة',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error searching audit logs:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في البحث',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'green',
      update: 'blue',
      delete: 'red',
    };
    return colors[action] || 'gray';
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      case: 'قضية',
      sample: 'عينة',
      trace: 'أثر',
    };
    return labels[entityType] || entityType;
  };

  const getFieldLabel = (fieldName, entityType) => {
    const labels = {
      case: {
        case_number: 'رقم القضية',
        reference_letter_number: 'رقم خطاب الإحالة',
        requesting_authority: 'الجهة الطالبة',
        letter_date: 'تاريخ الخطاب',
        letter_date_hijri: 'تاريخ الخطاب (هجري)',
        case_subject: 'موضوع القضية',
        category_id: 'تصنيف القضية',
        type_id: 'نوع القضية',
        created_by: 'منشئ القضية',
        location_id: 'الموقع',
        sample_image_path: 'مسار صورة العينة',
        trace_image_path: 'مسار صورة الأثر',
      },
      sample: {
        sample_number: 'رقم العينة',
        case_id: 'رقم القضية',
        surface_type: 'نوع السطح',
        description: 'الوصف',
        examination_material: 'مادة الفحص',
        sample_image: 'صورة العينة',
        sample_image_path: 'مسار صورة العينة',
        image_reference: 'مرجع الصورة',
      },
      trace: {
        trace_number: 'رقم الأثر',
        case_id: 'رقم القضية',
        sample_id: 'رقم العينة',
        status: 'الحالة',
        decision: 'القرار',
        name: 'الاسم',
        identity_number: 'رقم الهوية',
        notes: 'ملاحظات',
        trace_image: 'صورة الأثر',
        trace_image_path: 'مسار صورة الأثر',
      },
    };

    return labels[entityType]?.[fieldName] || fieldName;
  };

  const formatFieldValue = (fieldName, value, entityType, displayCaseNumber) => {
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.toLowerCase() === 'undefined')) {
      return 'غير محدد';
    }

    if (fieldName === 'case_id' && displayCaseNumber) {
      return displayCaseNumber;
    }

    if (fieldName === 'sample_image_path' || fieldName === 'trace_image_path') {
      return value && String(value).trim() !== '' ? 'مسار موجود' : 'غير محدد';
    }

    if (fieldName === 'surface_type') {
      const labels = {
        'Porous': 'مسامي',
        'Non-Porous': 'غير مسامي',
        'Semi-Porous': 'شبه مسامي',
      };
      return labels[value] || value;
    }

    if (fieldName === 'status') {
      const labels = {
        'Valid': 'صالح',
        'Invalid': 'غير صالح',
        'Suitable for Examination': 'صالح للفحص',
        'Not Suitable for Examination': 'غير صالح للفحص',
        'Under Examination': 'قيد الفحص',
        'Examination Completed': 'اكتمل الفحص',
      };
      return labels[value] || value;
    }

    if (fieldName === 'decision') {
      const labels = {
        'Match': 'تطابق',
        'Mismatch': 'عدم تطابق',
      };
      return labels[value] || value;
    }

    if (fieldName === 'sample_image' || fieldName === 'trace_image') {
      return value === '[Image Data]' ? 'صورة موجودة' : (value ? 'صورة موجودة' : 'لا توجد صورة');
    }

    if (typeof value === 'boolean') {
      return value ? 'نعم' : 'لا';
    }

    return String(value);
  };

  const FIELD_ORDER = {
    case: ['case_number', 'reference_letter_number', 'requesting_authority', 'letter_date', 'letter_date_hijri', 'case_subject', 'category_id', 'type_id', 'created_by', 'location_id', 'assignedUsers'],
    sample: ['sample_number', 'case_id', 'surface_type', 'description', 'examination_material', 'sample_image', 'sample_image_path', 'image_reference'],
    trace: ['trace_number', 'case_id', 'sample_id', 'status', 'decision', 'name', 'identity_number', 'notes', 'trace_image', 'trace_image_path'],
  };

  const getOrderedEntries = (data, entityType) => {
    if (!data || typeof data !== 'object') return [];
    const order = FIELD_ORDER[entityType];
    const entries = Object.entries(data);
    if (!order || order.length === 0) return entries;
    return entries.sort(([a], [b]) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };

  const renderReadableData = (data, entityType, displayCaseNumber) => {
    const entries = getOrderedEntries(data, entityType);
    if (entries.length === 0) {
      return (
        <Text fontSize="sm" color={emptyDataColor} fontStyle="italic">
          لا توجد بيانات
        </Text>
      );
    }

    return (
      <VStack align="stretch" spacing={2}>
        {entries.map(([key, value]) => (
          <Flex key={key} justify="space-between" align="flex-start" py={1} borderBottom="1px solid" borderColor={borderColor}>
            <Text fontSize="sm" fontWeight="semibold" color={labelColor} minW="150px">
              {getFieldLabel(key, entityType)}:
            </Text>
            <Text fontSize="sm" color={valueColor} textAlign="left" flex={1} dir="ltr">
              {formatFieldValue(key, value, entityType, displayCaseNumber)}
            </Text>
          </Flex>
        ))}
      </VStack>
    );
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>ملف عمليات القضية</Heading>

      <Card mb={6}>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
              <FormControl>
                <FormLabel>رقم القضية</FormLabel>
                <Input
                  placeholder="أدخل رقم القضية"
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                  dir="ltr"
                  textAlign="left"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>رقم خطاب الإحالة</FormLabel>
                <Input
                  placeholder="أدخل رقم خطاب الإحالة"
                  value={referenceLetterNumber}
                  onChange={(e) => setReferenceLetterNumber(e.target.value)}
                  dir="ltr"
                  textAlign="left"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </FormControl>
            </Flex>

            <Button
              colorScheme="primary"
              onClick={handleSearch}
              isLoading={loading}
              loadingText="جاري البحث..."
            >
              بحث
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {loading ? (
        <Center py={8}>
          <Spinner size="xl" />
        </Center>
      ) : logs.length > 0 ? (
        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="semibold" fontSize="lg" mb={2}>
                النتائج ({logs.length})
              </Text>
              {logs.map((log, index) => (
                <Box key={log.id} borderWidth="1px" borderRadius="md" p={4}>
                  <HStack justify="space-between" mb={2}>
                    <HStack spacing={2}>
                      <Badge colorScheme={getActionColor(log.action)}>
                        {log.actionLabel}
                      </Badge>
                      <Badge variant="outline">
                        {getEntityTypeLabel(log.entityType)}
                      </Badge>
                      {log.caseNumber && (
                        <Badge colorScheme="blue" variant="subtle">
                          {log.caseNumber}
                        </Badge>
                      )}
                    </HStack>
                    <VStack align="flex-end" spacing={0}>
                      {log.createdAtHijriFormatted && (
                        <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                          {log.createdAtHijriFormatted}
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.400">
                        {log.createdAtFormatted || log.createdAt}
                      </Text>
                    </VStack>
                  </HStack>
                  
                  {log.user && (
                    <Text fontSize="sm" color={userTextColor} mb={2}>
                      المستخدم: <strong>{log.user.fullName}</strong> ({log.user.username})
                    </Text>
                  )}

                  {log.referenceLetterNumber && (
                    <Text fontSize="sm" color={userTextColor} mb={2}>
                      رقم خطاب الإحالة: <strong dir="ltr">{log.referenceLetterNumber}</strong>
                    </Text>
                  )}

                  {log.description && (
                    <Text fontSize="sm" mb={2}>
                      {log.description}
                    </Text>
                  )}

                  {(log.oldValues || log.newValues) && (
                    <Accordion allowToggle>
                      {(log.oldValues && Object.keys(log.oldValues).length > 0) && (
                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="right">
                              القيم السابقة
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <Box
                              p={3}
                              borderRadius="md"
                              bg={oldValuesBg}
                              maxH="300px"
                              overflowY="auto"
                            >
                              {renderReadableData(log.oldValues, log.entityType, log.caseNumber)}
                            </Box>
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                      {(log.newValues && Object.keys(log.newValues).length > 0) && (
                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="right">
                              القيم الجديدة
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <Box
                              p={3}
                              borderRadius="md"
                              bg={newValuesBg}
                              maxH="300px"
                              overflowY="auto"
                            >
                              {renderReadableData(log.newValues, log.entityType, log.caseNumber)}
                            </Box>
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                    </Accordion>
                  )}

                  {index < logs.length - 1 && <Divider mt={4} />}
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      ) : null}
    </Box>
  );
};

export default CaseAuditLogSearch;

